import React, { useMemo } from "react";
import { renderToString } from "react-dom/server";
import { useMediaQuery, useTheme } from "@mui/material";
import Highcharts from "highcharts";
import { updateTooltipPosition } from "../helper";
import { CustomTooltip } from "./CustomTooltip";

export const useChartOptions = (
  chartData: any[] | undefined,
  height?: number
): Highcharts.Options => {
  const theme = useTheme();
  const isXS = useMediaQuery(theme.breakpoints.down("xs"));

  return useMemo(() => {
    return {
      title: {
        text: "",
      },
      credits: {
        enabled: false,
      },
      chart: {
        backgroundColor: "transparent",
        plotBackgroundColor: "transparent",
        plotShadow: false,
        type: "column",
        height,
      },
      xAxis: {
        type: "datetime",
        lineColor: theme.palette.graphVisual["08"].light,
        labels: {
          style: {
            color: theme.palette.graphVisual["08"].light,
          },
        },
      },
      yAxis: [
        {
          title: {
            text: "",
          },
          labels: {
            style: {
              color: theme.palette.graphVisual["08"].light,
            },
          },
          gridLineColor: theme.palette.divider,
        },
        {
          title: { text: "" },
          labels: {
            enabled: false,
          },
        },
      ],
      tooltip: {
        split: true,
        borderColor: "transparent",
        backgroundColor: "transparent",
        shadow: false,
        style: {
          boxShadow: "none",
          fontFamily: "var(--ff-font-family-base)",
          fontWeight: "400",
          fontSize: theme.typography.body3.fontSize as string,
          color: theme.palette.text.secondary,
        },
        useHTML: true,
        positioner: function (_, labelHeight, point) {
          return updateTooltipPosition(
            labelHeight,
            point,
            this.chart,
            isXS,
            ".custom-tooltip-container"
          );
        },
        formatter: function () {
          const value = this.points?.[0].key;
          const dateValue = new Date(value as string).getFullYear();
          const points =
            this.points?.map((point) => ({
              value: point.y as number,
              color: point.color as string,
              name: point.series.name,
            })) ?? [];

          return renderToString(
            <CustomTooltip
              points={points}
              theme={theme}
              label={dateValue.toString()}
            />
          );
        },
      },
      plotOptions: {
        series: {
          borderWidth: 0,
          dataLabels: {
            enabled: false,
          },
        },
        line: {
          marker: {
            enabled: false,
          },
          states: {
            hover: {
              enabled: false,
            },
          },
        },
      },
      series: chartData,
    };
  }, [chartData, theme, height, isXS]);
};
